// Date helpers and constants shared by the booking calendar and the availability screens.
// Weekday and month names are generated for the active language (Intl).

const LOCALES = { fr: "fr-CA", en: "en-CA", es: "es-ES" };
export const localeFor = (lang) => LOCALES[lang] || "en-CA";

// ── the booking status vocabulary ─────────────────────────────────────────────
// Mirrors `modules/booking/schemas.py`. ONE declaration for the whole practitioner
// booking surface (this module's colours, `BookingsAdmin`'s pills and its action gates),
// because the alternative is what F-25 residue R2 was: the grid kept its own literal
// exclusion list (`cancelled`, `paid_needs_attention`) and never learned about
// `completed`, so it offered a cancel the server refuses — and, on a consultation closed
// at zero, a cancel that would have SUCCEEDED and erased a delivered appointment.
//
// `LIVE_BOOKING_STATUSES` is the backend tuple of the same name: still to come, still
// payable, still movable. The terminal set is DERIVED from the two — never listed — so a
// status added to the vocabulary is live or terminal by construction, and cannot be
// silently absent from both.
export const LIVE_BOOKING_STATUSES = ["pending", "pending_payment", "confirmed", "paid_needs_attention"];
export const BOOKING_STATUSES = [...LIVE_BOOKING_STATUSES, "cancelled", "completed"];
export const TERMINAL_BOOKING_STATUSES =
  BOOKING_STATUSES.filter((s) => !LIVE_BOOKING_STATUSES.includes(s));

// The statuses under which a booking OCCUPIES its time (`CLAIMING_BOOKING_STATUSES` in
// the same schemas module): it holds its minutes, service plus buffer, and hides them
// from every other client. `paid_needs_attention` is out — that status means the time
// went to somebody else before the money arrived — and `completed` is in, because a
// delivered appointment's hour is never offered again. The calendar draws the trailing
// buffer only for these.
export const CLAIMING_BOOKING_STATUSES = ["pending", "pending_payment", "confirmed", "completed"];

// The statuses from which the practitioner may MOVE an appointment. The server refuses
// every other one with a named code (`_reschedule_state_refusal`); the calendar's drag
// and the Reschedule buttons are offered for these only, so a gesture the server would
// refuse is never offered in the first place.
export const MOVABLE_BOOKING_STATUSES = ["pending", "confirmed"];

// Resolved colours (concrete CSS values, not Tailwind classes) for FullCalendar's
// backgroundColor/borderColor/textColor props. FullCalendar applies them as inline styles
// on the .fc-event element, which sidesteps a specificity race: its own stylesheet is
// injected (CSS-in-JS) when the component mounts, i.e. AFTER the compiled Tailwind
// <link>, so at equal specificity (`.fc-v-event` against a utility class, 0-1-0 each) the
// later rule — always FullCalendar's — would win. An inline style has no such problem.
//
// Every foreground event is an APPOINTMENT or an external Google event: open hours are a
// background layer (`openHoursColor`), never an event of their own.
export const eventColor = (ev) => {
  if (ev.kind === "external") return { bg: "#F3F4F6", border: "#9CA3AF", text: "#4B5563" }; // gray-100/400/600
  const status = ev.booking?.status;
  if (status === "confirmed") return { bg: "color-mix(in srgb, var(--eh-sage) 40%, transparent)", border: "var(--eh-sage-deep)", text: "var(--eh-sage-deep)" };
  // A hold awaiting its payment is as tentative as an unconfirmed booking: same ochre.
  if (status === "pending" || status === "pending_payment") return { bg: "color-mix(in srgb, var(--eh-ochre) 25%, transparent)", border: "var(--eh-ochre-deep)", text: "var(--eh-ochre-deep)" };
  // Paid, but its time went to someone else: it needs a human, so it reads as an alert.
  if (status === "paid_needs_attention") return { bg: "#FEF3C7", border: "#D97706", text: "#92400E" }; // amber-100/600/800
  if (status === "cancelled") return { bg: "#FEE2E2", border: "#F87171", text: "#B91C1C" }; // red-100/400/700
  // F-25 — a consultation that has taken place and been settled. Slate, so a past
  // appointment reads as history rather than as a live confirmed one.
  return { bg: "#F1F5F9", border: "#94A3B8", text: "#475569" }; // slate-100/400/600 (completed)
};

// The background layer. FullCalendar draws a background event at its own
// `--fc-bg-event-opacity` (0.3), so these are the FULL colours and the library fades them.
// A PUBLISHED schedule is sage (clients can book inside it); a DRAFT one is ochre, the
// colour of the editor's own "Draft" badge, so the practitioner never reads hours nobody
// can book yet as open ones.
export const openHoursColor = (status) => (status === "published" ? "var(--eh-sage)" : "var(--eh-ochre)");

// The rest after an appointment: a faint slate block trailing it (same 0.3 fade).
export const BUFFER_COLOR = "#94A3B8"; // slate-400

// Long weekday names, Monday first (index 0..6 — the order of the backend weekly
// template's "0".."6" keys, "0" = Monday), localised.
export const weekdayLong = (lang) => {
  const fmt = new Intl.DateTimeFormat(localeFor(lang), { weekday: "long" });
  return Array.from({ length: 7 }, (_, i) => {
    const label = fmt.format(new Date(Date.UTC(2024, 0, 1 + i)));
    return label.replace(/^\w/, (c) => c.toUpperCase());
  });
};

// Backend refusal code (the 409/422 `detail`, a string, or the `code` of a dict-shaped
// one) -> i18n key. An unknown code — or a network error with no string detail — falls
// back to the caller's key. The reschedule door's own codes live in rescheduleErrors.js.
const ERROR_CODE_KEYS = {
  GCAL_NOT_CONNECTED: "err.gcalNotConnected",
  INVALID_WINDOWS: "err.invalidWindows",
  INVALID_TIMEZONE: "err.invalidTimezone",
  INVALID_RANGE: "err.invalidRange",
  INVALID_DATE: "err.invalidDate",
  TEMPLATE_NOT_FOUND: "err.templateNotFound",
  // Suspended practice: the console stays readable, writes are refused. Without this
  // mapping the practitioner saw a raw 403 on actions whose cause (billing) appeared
  // nowhere.
  TENANT_SUSPENDED: "err.tenantSuspended",
  // A per-minute consultation that has already been CHARGED cannot be cancelled: the
  // gateway owns that money (`consults.release_consult_hold`), and giving it back is a
  // refund with its own ceiling and its own ledger groups, not a status transition.
  ALREADY_COMPLETED: "err.consultCharged",
  // The practitioner's two OVERRIDABLE refusals of a move (`availability.py`): the target
  // is outside their own open hours, or on one of their own Google events. The calendar's
  // drag offers a retry with `allow_outside_availability` for exactly these two.
  OUTSIDE_AVAILABILITY: "err.outsideAvailability",
  TARGET_GCAL_BUSY: "err.targetGcalBusy",
  // The time is not offered (any more): taken since the page was drawn, in the past, or
  // overlapping another client. Never overridable — the claims forbid two clients on one
  // minute.
  TIME_UNAVAILABLE: "err.timeUnavailable",
  // B3 — money in flight: `pending_payment`/`paid_needs_attention` refuse some generic
  // transitions (service.py update_booking_status) with this bare code.
  PAYMENT_PENDING: "err.paymentPending",
  // Dict-shaped, carrying the offending `service_id`; the message does not interpolate it
  // (a raw catalogue id means nothing to the practitioner).
  INVALID_WINDOW_SERVICE: "err.invalidWindowService",
  // Dict-shaped, carrying the weekday key ("0".."6") of a window that offers no service —
  // neither its own nor a default. The weekly editor ALSO pins it on that day's row;
  // this sentence is what the toast says.
  WINDOW_WITHOUT_SERVICE: "err.windowWithoutService",
  // Dict-shaped, carrying the `date` of the extra hours a weekly window would overlap (and
  // its weekday key, which the editor uses to pin the sentence on that row).
  EXTRA_HOURS_OVERLAP: "err.extraHoursOverlap",
};

export const errMsg = (e, t, fallbackKey = "avail.error") => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") {
    return ERROR_CODE_KEYS[detail] ? t(ERROR_CODE_KEYS[detail]) : detail;
  }
  // Dict-shaped refusals carry a code plus context fields; render the mapped message with
  // any `{field}` placeholder filled in rather than collapsing to the generic fallback.
  if (detail && typeof detail === "object" && typeof detail.code === "string") {
    const key = ERROR_CODE_KEYS[detail.code];
    if (key) {
      let msg = t(key);
      for (const [k, v] of Object.entries(detail)) {
        if (k !== "code") msg = msg.replaceAll(`{${k}}`, String(v));
      }
      return msg;
    }
    return detail.message || detail.code;
  }
  return t(fallbackKey);
};

// Relative timestamp ("il y a 5 min" / "5 min ago" / "hace 5 min") in coarse units
// (seconds/minutes/hours/days), localised through Intl.RelativeTimeFormat. Used for
// "busy_checked_at" in the sync status strip (Phase 7).
export const relativeTime = (iso, lang = "fr") => {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(diffMs)) return null;
  const rtf = new Intl.RelativeTimeFormat(localeFor(lang), { numeric: "auto" });
  const abs = Math.abs(diffMs);
  const SEC = 1000, MIN = 60 * SEC, HOUR = 60 * MIN, DAY = 24 * HOUR;
  if (abs < MIN) return rtf.format(Math.round(diffMs / SEC), "second");
  if (abs < HOUR) return rtf.format(Math.round(diffMs / MIN), "minute");
  if (abs < DAY) return rtf.format(Math.round(diffMs / HOUR), "hour");
  return rtf.format(Math.round(diffMs / DAY), "day");
};
